import * as ExifReader from "exifreader"
import * as utif from "utif"

import { reportSentry } from "@/utils/sentry"

/**
 * Get image size in inches as rounded integers: [widthInches, heightInches]
 * - Needs EXIF DPI (JPEG/TIFF). Returns [0, 0] when it is missing — there is no
 *   96 DPI fallback: a guessed DPI would print a wrong physical size, which is
 *   worse than saying we could not measure it. Callers must treat [0, 0] as
 *   "unknown" and must not render it as a dimension (see PrintFileView.vue).
 * - Only needs a URL string
 */
export async function getImageSizeInches(url: string): Promise<[number, number]> {
  const { w, h } = await loadPixels(url)
  const exifDpi = await readExifDpi(url)

  if (!exifDpi) {
    return [0, 0]
  }

  const dpi = exifDpi
  const inchesW = Math.round((w / dpi) * 10) / 10
  const inchesH = Math.round((h / dpi) * 10) / 10
  return [inchesW, inchesH]
}

function loadPixels(url: string): Promise<{ w: number; h: number }> {
  // Keep the signed download query for requests; only inspect the file path.
  const imagePath = url.split(/[?#]/, 1)[0]!.toLowerCase()
  if (imagePath.endsWith(".tif") || imagePath.endsWith(".tiff")) {
    return fetch(url, { mode: "cors", cache: "no-store" })
      .then((res) => res.arrayBuffer())
      .then((buf) => {
        const ifds = utif.decode(buf)
        utif.decodeImage(buf, ifds[0]!)
        const firstIfd = ifds[0]!
        const width = firstIfd.width
        const height = firstIfd.height
        return { w: width, h: height }
      })
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
    img.onerror = () => {
      const error = new Error("Failed to load image")
      reportSentry(error, imageLoadContext(url))
      reject(error)
    }
    img.src = url
  })
}

function imageLoadContext(url: string) {
  const context: { image_type: string; signed_aws_request: boolean | null; response_status: number | null } = {
    image_type: "unknown",
    signed_aws_request: null,
    response_status: null,
  }
  try {
    const parsed = new URL(url, window.location.href)
    const extension = parsed.pathname.match(/\.(jpe?g|png|tiff?|heic|heif|webp|gif|bmp|svg|avif)$/i)
    context.image_type = extension?.[1]?.toLowerCase() || "unknown"
    context.signed_aws_request = parsed.searchParams.has("X-Amz-Signature") || parsed.searchParams.has("Signature")
    const entries = performance.getEntriesByName(parsed.href)
    const resource = entries[entries.length - 1] as (PerformanceResourceTiming & { responseStatus?: number }) | undefined
    const status = resource?.responseStatus
    if (status && status >= 100 && status <= 599) context.response_status = status
  } catch {
    // Missing Resource Timing or an invalid URL must not replace the load error.
  }
  // Never include the URL, filename, signature, image bytes, or other request data.
  return context
}

async function readExifDpi(url: string) {
  try {
    const res = await fetch(url, { mode: "cors", cache: "no-store" })
    const buf = await res.arrayBuffer()
    const meta = await ExifReader.load(buf)
    const unit = meta?.ResolutionUnit?.value
    // const conv = unit === 3 ? 2.54 : 1
    const xRes = Number(meta.XResolution!.description)
    // const yRes = Number(meta.YResolution!.description)

    if (!xRes || !unit) return null

    const isCm = unit === 3 || (typeof unit === "string" && unit.toLowerCase().startsWith("centimeter"))

    const dpi = isCm ? xRes * 2.54 : xRes
    return isFinite(dpi) && dpi > 0 ? dpi : null
  } catch (e) {
    console.error("Failed to read EXIF DPI:", e)
    reportSentry(e)
    return null
  }
}
